import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ApiService } from '@portal-core/auth/services/api.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { CreateProjectTemplate } from '@portal-core/projects/create-project/models/create-project-template.model';
import { ProjectStatus } from '@portal-core/projects/enums/project-status.enum';
import { ProjectDependencies } from '@portal-core/projects/models/project-dependencies.model';
import { ProjectReportScanStatusWithLatestNotificationId } from '@portal-core/projects/models/project-report-scan-status-with-latest-notification-id.model';
import { Project } from '@portal-core/projects/models/project.model';
import { Team } from '@portal-core/teams/models/team.model';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProjectsApiService {
  constructor(private http: HttpClient, private apiService: ApiService) { }

  @SharePendingRequest()
  getProjectById$(projectId: number): Observable<Project> {
    return this.http.get<Project>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}`);
  }

  @SharePendingRequest()
  getProjectsByIds$(projectIds: number[]): Observable<Project[]> {
    return this.http.post<Project[]>(`${this.apiService.centralApiBaseUri}/api/projects/getBatchProjectsByIds`, projectIds);
  }

  @SharePendingRequest()
  getProjectsPageByLicenseId$(licenseId: number, filter: PageFilter, maxIncludes?: number): Observable<Page<Project>> {
    return this.http.post<Page<Project>>(`${this.apiService.centralApiBaseUri}/api/projects/license/${licenseId}/getMyProjectsByLicenseList${typeof maxIncludes === 'number' ? '?maxIncludes=' + maxIncludes : ''}`, filter);
  }

  @SharePendingRequest()
  getReviewProjectsPageByLicenseId$(licenseId: number, filter: PageFilter): Observable<Page<Project>> {
    return this.http.post<Page<Project>>(`${this.apiService.centralApiBaseUri}/api/Reviews/Projects/${licenseId}`, filter);
  }

  @SharePendingRequest()
  checkProjectById$(projectId: number): Observable<Project> {
    return this.http.get<Project>(`${this.apiService.centralApiBaseUri}/api/projects/userCheck/${projectId}`);
  }

  @SharePendingRequest()
  getProjectListItemsByLicenseId$(licenseId: number): Observable<Project[]> {
    return this.http.get<Project[]>(`${this.apiService.centralApiBaseUri}/api/projects/license/${licenseId}/getProjectsList`);
  }

  @SharePendingRequest()
  getMyProjectListItemsByLicenseId$(licenseId: number): Observable<Project[]> {
    return this.http.get<Project[]>(`${this.apiService.centralApiBaseUri}/api/projects/license/${licenseId}/getMyProjectsByLicenseLite`).pipe(
      map(projects => projects.filter(project => project.UserHasAccess))
    );
  }

  @SharePendingRequest()
  getProjectTeamsPage$(projectId: number, filter: PageFilter): Observable<Page<Team>> {
    return this.http.post<Page<Team>>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/teams`, filter);
  }

  @SharePendingRequest()
  getProjectLicenseUsersPage$(projectId: number, filter: PageFilter): Observable<Page<LicenseUser>> {
    return this.http.post<Page<LicenseUser>>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/licenseusers`, filter);
  }

  updateProject$(project: Project): Observable<Project> {
    return this.http.put<Project>(`${this.apiService.centralApiBaseUri}/api/Projects/updateProjectSettings`, project);
  }

  updateProjectTeamsAndUsers$(project: Project): Observable<Project> {
    return this.http.put<Project>(`${this.apiService.centralApiBaseUri}/api/Projects/updateProjectTeamsAndUsers`, project);
  }

  @SharePendingRequest()
  getProjectsList$(licenseId: number): Observable<Project[]> {
    return this.http.get<Project[]>(`${this.apiService.centralApiBaseUri}/api/projects/license/${licenseId}/getProjectsList`);
  }

  @SharePendingRequest()
  getProjectReportScanStatus$(projectId: number, branchName?: string): Observable<ProjectReportScanStatusWithLatestNotificationId> {
    let params = new HttpParams();
    if (branchName) {
      params = params.set('branchName', branchName);
    }
    return this.http.get<ProjectReportScanStatusWithLatestNotificationId>(`${this.apiService.centralApiBaseUri}/api/analyzer/scanstatus/${projectId}`, { params });
  }

  putProjectStatus$(projectId: number, status: ProjectStatus): Observable<any> {
    return this.http.put<any>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/changeStatus`, { Status: status });
  }

  analyzeProject$(projectId: number, branchName: string): Observable<any> {
    return this.http.post(`${this.apiService.centralApiBaseUri}/api/analyzer/scanproject/${projectId}?branchName=${encodeURIComponent(branchName)}`, null);
  }

  deleteProjectLicenseUser$(projectId: number, licenseUserId: number): Observable<any> {
    return this.http.delete(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/licenseuser/${licenseUserId}`);
  }

  deleteProjectTeam$(projectId: number, teamId: number): Observable<any> {
    return this.http.delete(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/team/${teamId}`);
  }

  // Using POST method instead of DELETE because Angular http.delete doesn't support request body https://angular.io/api/common/http/HttpClient
  bulkDeleteProjects$(projectIds: number[]): Observable<any> {
    return this.http.post(`${this.apiService.centralApiBaseUri}/api/projects/bulkDeleteProjects`, projectIds);
  }

  bulkUpdateProjectStatus$(status: ProjectStatus, projectIds: number[]): Observable<any> {
    return this.http.put(`${this.apiService.centralApiBaseUri}/api/projects/bulkUpdateStatus/${status}`, projectIds);
  }

  addTeamsToProject$(projectId: number, teamIds: number[]): Observable<MadCloudResult> {
    return this.http.post<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/BulkAddTeams`, teamIds);
  }

  addLicenseUsersToProject$(projectId: number, licenseUserIds: number[]): Observable<MadCloudResult> {
    return this.http.post<MadCloudResult>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/BulkAddLicenseUsers`, licenseUserIds);
  }

  addLicenseUserToProjects$(licenseUserId: number, projectIds: number[]): Observable<any> {
    return this.http.post(`${this.apiService.centralApiBaseUri}/api/projects/licenseUser/${licenseUserId}/addLicenseUserToProjects`, projectIds);
  }

  @SharePendingRequest()
  getProjectDependencies$(projectId: number): Observable<ProjectDependencies> {
    return this.http.get<ProjectDependencies>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/dependencies`);
  }

  @SharePendingRequest()
  getProjectLanguage$(projectId: number, commitId: string): Observable<string> {
    return this.http.get<string>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/projectLanguage?commitId=${encodeURIComponent(commitId)}`);
  }

  @SharePendingRequest()
  getTargetLanguage$(projectId: number, commitId: string, targetPath: string): Observable<string> {
    return this.http.get<string>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/targetLanguage?commitId=${encodeURIComponent(commitId)}&targetPath=${encodeURIComponent(targetPath)}`);
  }

  getProjectArchivePath(projectId: number, commitId: string): string {
    return `${this.apiService.centralArchiveBaseUri}/project/${projectId}/${commitId}`;
  }

  createProject$(licenseId: number, project: FormData): Observable<Project> {
    return this.http.post<Project>(`${this.apiService.centralApiBaseUri}/api/projects/${licenseId}/create`, project);
  }

  @SharePendingRequest()
  validateProjectName$(licenseId: number, projectName: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiService.centralApiBaseUri}/api/projects/license/${licenseId}/validateProjectName?projectName=${encodeURIComponent(projectName)}`);
  }

  @SharePendingRequest()
  getProjectTemplates$(): Observable<CreateProjectTemplate[]> {
    return this.http.get<CreateProjectTemplate[]>(`${this.apiService.centralApiBaseUri}/api/projects/projectTemplates`);
  }
}
